import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Lock, CheckCircle, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router';
import toast from 'react-hot-toast';

const Activate = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [activationSuccess, setActivationSuccess] = useState(false);

    const activateMutation = useMutation({
        mutationFn: async (data: { token: string; password: string; confirmPassword: string }) => {
            const response = await api.post('/auth/activate', data);
            return response.data;
        },
        onSuccess: (data) => {
            setActivationSuccess(true);
            toast.success('Account activated successfully!');

            // Log the user in automatically
            if (data.accessToken && data.refreshToken) {
                // Store tokens in localStorage (same pattern as useLogin)
                localStorage.setItem('accessToken', data.accessToken);
                localStorage.setItem('refreshToken', data.refreshToken);

                // Invalidate and refetch user data
                queryClient.invalidateQueries({ queryKey: ['user'] });

                setTimeout(() => {
                    navigate('/dashboard');
                }, 2000);
            } else {
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            }
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Failed to activate account';
            toast.error(message);
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            toast.error('Invalid activation link');
            return;
        }

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        // Check password requirements
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);

        if (!hasUpperCase || !hasLowerCase || !hasNumber) {
            toast.error(
                'Password must contain at least one uppercase letter, one lowercase letter, and one number',
            );
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        activateMutation.mutate({ token, password, confirmPassword });
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-linear-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md p-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                            <AlertCircle className="w-10 h-10 text-red-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            Invalid Activation Link
                        </h1>
                        <p className="text-gray-600 mb-6">
                            This account activation link is invalid or has expired. Please contact
                            your administrator to request a new invitation.
                        </p>
                        <Link to="/">
                            <Button className="w-full">Go to Login</Button>
                        </Link>
                    </div>
                </Card>
            </div>
        );
    }

    if (activationSuccess) {
        return (
            <div className="min-h-screen bg-linear-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md p-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            Account Activated!
                        </h1>
                        <p className="text-gray-600 mb-6">
                            Your account has been successfully activated. You're being redirected to
                            your dashboard...
                        </p>
                        <Link to="/dashboard">
                            <Button className="w-full">Go to Dashboard</Button>
                        </Link>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-linear-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md p-8">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Set Up Your Account</h1>
                    <p className="text-gray-600">
                        Welcome! Create a password to activate your account.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <Label htmlFor="password">Password</Label>
                        <div className="relative mt-1">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Create a strong password"
                                className="pl-10 pr-10"
                                required
                                minLength={8}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword ? (
                                    <EyeOff className="w-5 h-5" />
                                ) : (
                                    <Eye className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Minimum 8 characters with uppercase, lowercase, and number
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="confirmPassword">Confirm Password</Label>
                        <div className="relative mt-1">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                id="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter your password"
                                className="pl-10 pr-10"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showConfirmPassword ? (
                                    <EyeOff className="w-5 h-5" />
                                ) : (
                                    <Eye className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        disabled={activateMutation.isPending}
                        className="w-full bg-linear-to-r from-green-400 to-blue-500 hover:opacity-90"
                    >
                        {activateMutation.isPending ? 'Activating...' : 'Activate Account'}
                    </Button>
                </form>

                <div className="mt-6 text-center">
                    <Link to="/" className="text-sm text-blue-600 hover:text-blue-500 font-medium">
                        Already have an account? Sign in
                    </Link>
                </div>
            </Card>
        </div>
    );
};

export default Activate;
